import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DayTableSimple from '../DayTableSimple';
import ScheduleEntry from '../../models/ScheduleEntry';
import { SharedScrollProvider } from '../../contexts/SharedScrollContext';
import { KEY_SEPARATOR } from '../../hooks/useScheduleDragAndDrop';
import { createEmptyDraggedEntry } from '../../types/dragAndDrop';
import type Teacher from '../../models/Teacher';
import type Group from '../../models/Group';
import type Subject from '../../models/Subject';
import type Room from '../../models/Room';
import { formatDraftOptionLabel } from '../../utils/scheduleDraftKind';

const EMPTY_DRAG_INFO = createEmptyDraggedEntry();
const MARQUEE_DRAG_THRESHOLD_PX = 5;

interface MarqueeState {
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
}

const normalizeClientRect = (x1: number, y1: number, x2: number, y2: number) => ({
    left: Math.min(x1, x2),
    top: Math.min(y1, y2),
    right: Math.max(x1, x2),
    bottom: Math.max(y1, y2),
});

const rectsIntersect = (
    a: { left: number; top: number; right: number; bottom: number },
    b: { left: number; top: number; right: number; bottom: number }
) => !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);

const findEntryIdsInClientRect = (container: HTMLElement, selectRect: ReturnType<typeof normalizeClientRect>) => {
    const ids: string[] = [];
    container.querySelectorAll('[data-schedule-entry-id]').forEach((element) => {
        const entryId = element.getAttribute('data-schedule-entry-id');
        if (!entryId) return;
        const rect = element.getBoundingClientRect();
        if (rectsIntersect(rect, selectRect)) {
            ids.push(entryId);
        }
    });
    return ids;
};

interface DraftOption {
    draft_name: string;
    display_name?: string | null;
    version_number?: number | null;
    period_id?: string | number | null;
    periodId?: string | number | null;
}

interface CopyHistoryBatch {
    targetDraftName: string;
    entries: ScheduleEntry[];
}

interface CopyDraftViewProps {
    assetManager: {
        fetchScheduleEntriesForDraft: (draftName: string) => Promise<ScheduleEntry[]>;
        createScheduleEntry: (data: Record<string, unknown>) => Promise<ScheduleEntry>;
        deleteScheduleEntry: (entryId: string | number) => Promise<void>;
    };
    drafts: DraftOption[];
    currentDraftName: string | null;
    activeDays: string[];
    allRoomsForGrid: Room[];
    user: { role: string };
    teachers: Teacher[];
    groups: Group[];
    subjects: Subject[];
    selectedWeek?: { week: number; year?: number } | null;
    selectedYearForView?: number;
    getViewFilteredEntries: (entries: ScheduleEntry[]) => ScheduleEntry[];
    onCopyToCurrentDraft?: (entry: ScheduleEntry) => void;
    onRemoveFromCurrentDraft?: (entryId: string | number) => void;
    onError?: (message: string) => void;
    onSuccess?: (message: string) => void;
}

type PanelSide = 'left' | 'right';

const getDraftPeriodId = (draft: DraftOption | null | undefined) => {
    const raw = draft?.period_id ?? draft?.periodId;
    if (raw === null || raw === undefined || raw === '') return null;
    return raw;
};

const duplicateEntryForDraft = (sourceEntry: ScheduleEntry, targetDraftName: string): ScheduleEntry => {
    const sourceObj = typeof sourceEntry?.toObject === 'function'
        ? sourceEntry.toObject()
        : { ...(sourceEntry as unknown as Record<string, unknown>) };
    const newItemId = `item-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    const newItemData = {
        ...sourceObj,
        id: newItemId,
        draft_name: targetDraftName,
    };
    return new ScheduleEntry(newItemData);
};

const CopyDraftView: React.FC<CopyDraftViewProps> = ({
    assetManager,
    drafts,
    currentDraftName,
    activeDays,
    allRoomsForGrid,
    user,
    teachers,
    groups,
    subjects,
    selectedWeek,
    selectedYearForView,
    getViewFilteredEntries,
    onCopyToCurrentDraft,
    onRemoveFromCurrentDraft,
    onError,
    onSuccess,
}) => {
    const draftOptions = useMemo(
        () => (drafts || []).filter((draft) => Boolean(draft?.draft_name)),
        [drafts]
    );

    const [leftDraftName, setLeftDraftName] = useState<string>(() => (
        currentDraftName || draftOptions[0]?.draft_name || ''
    ));
    const [rightDraftName, setRightDraftName] = useState<string>(() => {
        const fallback = draftOptions.find((draft) => draft.draft_name !== currentDraftName)?.draft_name
            || draftOptions[1]?.draft_name
            || draftOptions[0]?.draft_name
            || '';
        return fallback;
    });

    const [leftEntries, setLeftEntries] = useState<ScheduleEntry[]>([]);
    const [rightEntries, setRightEntries] = useState<ScheduleEntry[]>([]);
    const [leftSelectedIds, setLeftSelectedIds] = useState<string[]>([]);
    const [leftLoading, setLeftLoading] = useState(false);
    const [rightLoading, setRightLoading] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [isHistoryAction, setIsHistoryAction] = useState(false);
    const [undoStack, setUndoStack] = useState<CopyHistoryBatch[]>([]);
    const [redoStack, setRedoStack] = useState<CopyHistoryBatch[]>([]);
    const [highlightedItemId, setHighlightedItemId] = useState<string | number | null>(null);
    const [marquee, setMarquee] = useState<MarqueeState | null>(null);
    const leftPanelScrollRef = useRef<HTMLDivElement>(null);
    const rightPanelScrollRef = useRef<HTMLDivElement>(null);
    const isSyncingVerticalScrollRef = useRef(false);
    const marqueeActiveRef = useRef(false);

    const handlePanelScroll = useCallback((source: PanelSide) => {
        if (isSyncingVerticalScrollRef.current) return;

        const sourceEl = source === 'left' ? leftPanelScrollRef.current : rightPanelScrollRef.current;
        const targetEl = source === 'left' ? rightPanelScrollRef.current : leftPanelScrollRef.current;
        if (!sourceEl || !targetEl) return;

        isSyncingVerticalScrollRef.current = true;
        targetEl.scrollTop = sourceEl.scrollTop;
        requestAnimationFrame(() => {
            isSyncingVerticalScrollRef.current = false;
        });
    }, []);

    useEffect(() => {
        if (!leftDraftName && draftOptions[0]?.draft_name) {
            setLeftDraftName(draftOptions[0].draft_name);
        }
        if (!rightDraftName && draftOptions.length > 1) {
            const alt = draftOptions.find((draft) => draft.draft_name !== leftDraftName);
            if (alt?.draft_name) setRightDraftName(alt.draft_name);
        }
    }, [draftOptions, leftDraftName, rightDraftName]);

    const loadDraftEntries = useCallback(async (draftName: string, side: PanelSide) => {
        if (!draftName || !assetManager?.fetchScheduleEntriesForDraft) return;
        const setLoading = side === 'left' ? setLeftLoading : setRightLoading;
        const setEntries = side === 'left' ? setLeftEntries : setRightEntries;
        setLoading(true);
        try {
            const entries = await assetManager.fetchScheduleEntriesForDraft(draftName);
            const processed = (entries || []).map((entry) => {
                const entryData = typeof entry.toObject === 'function' ? entry.toObject() : { ...entry };
                return new ScheduleEntry({
                    ...entryData,
                    weekSeries: entryData.weekSeries || '',
                });
            });
            setEntries(processed);
        } catch (error) {
            console.error('[CopyDraftView] Failed to load draft entries:', error);
            onError?.(`Failed to load draft "${draftName}"`);
            setEntries([]);
        } finally {
            setLoading(false);
        }
    }, [assetManager, onError]);

    useEffect(() => {
        if (leftDraftName) {
            loadDraftEntries(leftDraftName, 'left');
            setLeftSelectedIds([]);
        }
    }, [leftDraftName, loadDraftEntries]);

    useEffect(() => {
        if (rightDraftName) {
            loadDraftEntries(rightDraftName, 'right');
        }
    }, [rightDraftName, loadDraftEntries]);

    useEffect(() => {
        setUndoStack([]);
        setRedoStack([]);
    }, [rightDraftName]);

    useEffect(() => {
        if (leftLoading || rightLoading) return;
        const leftEl = leftPanelScrollRef.current;
        const rightEl = rightPanelScrollRef.current;
        if (!leftEl || !rightEl) return;
        rightEl.scrollTop = leftEl.scrollTop;
    }, [leftLoading, rightLoading, leftDraftName, rightDraftName]);

    const leftFilteredEntries = useMemo(
        () => getViewFilteredEntries(leftEntries),
        [leftEntries, getViewFilteredEntries]
    );
    const rightFilteredEntries = useMemo(
        () => getViewFilteredEntries(rightEntries),
        [rightEntries, getViewFilteredEntries]
    );

    const leftSelectedEntries = useMemo(
        () => {
            const selectedSet = new Set(leftSelectedIds);
            return leftFilteredEntries.filter((entry) => selectedSet.has(String(entry.id)));
        },
        [leftFilteredEntries, leftSelectedIds]
    );

    const persistCopiedEntry = useCallback(async (
        sourceEntry: ScheduleEntry,
        targetDraftName: string,
        options: { notify?: boolean } = {}
    ) => {
        const { notify = true } = options;
        const localDuplicate = duplicateEntryForDraft(sourceEntry, targetDraftName);
        const createData = localDuplicate.toObject();
        delete (createData as Record<string, unknown>).id;

        const savedEntry = await assetManager.createScheduleEntry(createData);
        setRightEntries((prev) => [...prev, savedEntry]);

        if (currentDraftName && targetDraftName === currentDraftName) {
            onCopyToCurrentDraft?.(savedEntry);
        }

        if (notify) {
            setHighlightedItemId(savedEntry.id);
            window.setTimeout(() => setHighlightedItemId(null), 2000);
            onSuccess?.(`Copied entry to "${targetDraftName}"`);
        }
        return savedEntry;
    }, [assetManager, currentDraftName, onCopyToCurrentDraft, onSuccess]);

    const handleCopyLeftToRight = useCallback(async () => {
        if (leftSelectedEntries.length === 0 || !rightDraftName || isCopying || isHistoryAction) return;
        setIsCopying(true);
        try {
            const savedEntries: ScheduleEntry[] = [];
            for (const entry of leftSelectedEntries) {
                const savedEntry = await persistCopiedEntry(
                    entry,
                    rightDraftName,
                    { notify: false }
                );
                savedEntries.push(savedEntry);
            }
            const lastSaved = savedEntries[savedEntries.length - 1];
            if (lastSaved) {
                setHighlightedItemId(lastSaved.id);
                window.setTimeout(() => setHighlightedItemId(null), 2000);
            }
            setUndoStack((prev) => [...prev, { targetDraftName: rightDraftName, entries: savedEntries }]);
            setRedoStack([]);
            const count = leftSelectedEntries.length;
            onSuccess?.(`Copied ${count} ${count === 1 ? 'entry' : 'entries'} to "${rightDraftName}"`);
            setLeftSelectedIds([]);
        } catch (error) {
            console.error('[CopyDraftView] Copy left-to-right failed:', error);
            onError?.('Failed to copy entries to the target draft');
        } finally {
            setIsCopying(false);
        }
    }, [leftSelectedEntries, rightDraftName, isCopying, isHistoryAction, persistCopiedEntry, onError, onSuccess]);

    const handleUndo = useCallback(async () => {
        if (undoStack.length === 0 || isCopying || isHistoryAction) return;
        const batch = undoStack[undoStack.length - 1];
        if (!batch || batch.targetDraftName !== rightDraftName) return;

        setIsHistoryAction(true);
        try {
            for (const entry of batch.entries) {
                await assetManager.deleteScheduleEntry(entry.id);
                setRightEntries((prev) => prev.filter((item) => String(item.id) !== String(entry.id)));
                if (currentDraftName && batch.targetDraftName === currentDraftName) {
                    onRemoveFromCurrentDraft?.(entry.id);
                }
            }
            setUndoStack((prev) => prev.slice(0, -1));
            setRedoStack((prev) => [...prev, batch]);
            onSuccess?.('Undid last copy');
        } catch (error) {
            console.error('[CopyDraftView] Undo failed:', error);
            onError?.('Failed to undo last copy');
        } finally {
            setIsHistoryAction(false);
        }
    }, [
        undoStack,
        isCopying,
        isHistoryAction,
        rightDraftName,
        assetManager,
        currentDraftName,
        onRemoveFromCurrentDraft,
        onSuccess,
        onError,
    ]);

    const handleRedo = useCallback(async () => {
        if (redoStack.length === 0 || isCopying || isHistoryAction) return;
        const batch = redoStack[redoStack.length - 1];
        if (!batch || batch.targetDraftName !== rightDraftName) return;

        setIsHistoryAction(true);
        try {
            const recreatedEntries: ScheduleEntry[] = [];
            for (const entry of batch.entries) {
                const createData = entry.toObject();
                delete (createData as Record<string, unknown>).id;
                const savedEntry = await assetManager.createScheduleEntry(createData);
                recreatedEntries.push(savedEntry);
                setRightEntries((prev) => [...prev, savedEntry]);
                if (currentDraftName && batch.targetDraftName === currentDraftName) {
                    onCopyToCurrentDraft?.(savedEntry);
                }
            }
            const lastSaved = recreatedEntries[recreatedEntries.length - 1];
            if (lastSaved) {
                setHighlightedItemId(lastSaved.id);
                window.setTimeout(() => setHighlightedItemId(null), 2000);
            }
            setRedoStack((prev) => prev.slice(0, -1));
            setUndoStack((prev) => [...prev, { targetDraftName: batch.targetDraftName, entries: recreatedEntries }]);
            onSuccess?.('Redid last copy');
        } catch (error) {
            console.error('[CopyDraftView] Redo failed:', error);
            onError?.('Failed to redo last copy');
        } finally {
            setIsHistoryAction(false);
        }
    }, [
        redoStack,
        isCopying,
        isHistoryAction,
        rightDraftName,
        assetManager,
        currentDraftName,
        onCopyToCurrentDraft,
        onSuccess,
        onError,
    ]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;
            if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) {
                return;
            }

            if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
                event.preventDefault();
                void handleUndo();
            } else if (
                (event.ctrlKey && event.key === 'y')
                || ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'z')
            ) {
                event.preventDefault();
                void handleRedo();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo, handleRedo]);

    const applySelectionUpdate = useCallback((
        ids: string[],
        options: { additive?: boolean; replace?: boolean } = {}
    ) => {
        setLeftSelectedIds((prev) => {
            if (options.replace) {
                return ids;
            }
            if (options.additive) {
                const next = new Set(prev);
                ids.forEach((id) => next.add(id));
                return Array.from(next);
            }
            return ids;
        });
    }, []);

    const handleLeftSelect = useCallback((entry: ScheduleEntry, event: React.MouseEvent) => {
        const id = String(entry.id);
        const isToggle = event.metaKey || event.ctrlKey;

        setLeftSelectedIds((prev) => {
            if (isToggle) {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return Array.from(next);
            }
            return [id];
        });
    }, []);

    const handleSourcePanelMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        if ((event.target as Element).closest('[data-schedule-entry-id]')) return;
        if ((event.target as Element).closest('select, button, a, input, textarea, label')) return;

        const container = leftPanelScrollRef.current;
        if (!container) return;

        event.preventDefault();
        marqueeActiveRef.current = false;

        const startX = event.clientX;
        const startY = event.clientY;
        setMarquee({ startX, startY, currentX: startX, currentY: startY });

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaX = Math.abs(moveEvent.clientX - startX);
            const deltaY = Math.abs(moveEvent.clientY - startY);
            if (deltaX > MARQUEE_DRAG_THRESHOLD_PX || deltaY > MARQUEE_DRAG_THRESHOLD_PX) {
                marqueeActiveRef.current = true;
            }
            setMarquee({
                startX,
                startY,
                currentX: moveEvent.clientX,
                currentY: moveEvent.clientY,
            });
        };

        const handleMouseUp = (upEvent: MouseEvent) => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.removeProperty('user-select');
            setMarquee(null);

            if (marqueeActiveRef.current) {
                const selectRect = normalizeClientRect(startX, startY, upEvent.clientX, upEvent.clientY);
                const ids = findEntryIdsInClientRect(container, selectRect);
                applySelectionUpdate(ids, { additive: upEvent.metaKey || upEvent.ctrlKey });
                return;
            }

            if (!upEvent.metaKey && !upEvent.ctrlKey) {
                setLeftSelectedIds([]);
            }
        };

        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [applySelectionUpdate]);

    const renderDraftSelector = (
        side: PanelSide,
        value: string,
        onChange: (draftName: string) => void,
        otherDraftName: string
    ) => (
        <div className="flex items-center gap-2 mb-3 px-1">
            <label htmlFor={`copy-view-draft-${side}`} className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {side === 'left' ? 'Source draft' : 'Target draft'}
            </label>
            <select
                id={`copy-view-draft-${side}`}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                className="flex-1 min-w-0 text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                {draftOptions.map((draft) => (
                    <option key={draft.draft_name} value={draft.draft_name}>
                        {formatDraftOptionLabel(draft) || draft.draft_name}
                        {getDraftPeriodId(draft) != null ? '' : ' (unscoped)'}
                    </option>
                ))}
            </select>
            {value === otherDraftName && draftOptions.length > 1 && (
                <span className="text-xs text-amber-700 whitespace-nowrap">Same draft</span>
            )}
        </div>
    );

    const renderPanel = (
        side: PanelSide,
        draftName: string,
        filteredEntries: ScheduleEntry[],
        selectedIds: string[],
        onSelect: ((entry: ScheduleEntry, event: React.MouseEvent) => void) | null,
        isLoading: boolean,
        onPanelMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void
    ) => (
        <div className="flex-1 min-w-0 flex flex-col">
            {renderDraftSelector(
                side,
                draftName,
                side === 'left' ? setLeftDraftName : setRightDraftName,
                side === 'left' ? rightDraftName : leftDraftName
            )}
            <div
                ref={side === 'left' ? leftPanelScrollRef : rightPanelScrollRef}
                className={`flex-1 overflow-y-auto min-h-0 ${onPanelMouseDown ? 'select-none' : ''}`}
                onScroll={() => handlePanelScroll(side)}
                onMouseDown={onPanelMouseDown}
            >
                {isLoading ? (
                    <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
                        Loading draft…
                    </div>
                ) : (
                    <div className="space-y-2">
                        {activeDays.map((day) => {
                            const itemsForDay = filteredEntries.filter(
                                (item) => item.day === day && item.isScheduled()
                            );
                            return (
                                <DayTableSimple
                                    key={`${side}-${day}-${draftName}`}
                                    day={day}
                                    rooms={allRoomsForGrid}
                                    items={itemsForDay}
                                    dayScheduleEntry={filteredEntries}
                                    user={user}
                                    onOpenModal={() => { }}
                                    onItemDrop={() => { }}
                                    draggedItemInfo={EMPTY_DRAG_INFO}
                                    onGlobalDragStart={() => { }}
                                    onGlobalDragEnd={() => { }}
                                    KEY_SEPARATOR={KEY_SEPARATOR}
                                    teachers={teachers}
                                    groups={groups}
                                    subjects={subjects}
                                    currentWeekNumber={selectedWeek?.week}
                                    currentYear={selectedWeek?.year || selectedYearForView}
                                    showMondayHeader={day === 'monday'}
                                    readOnly
                                    selectionMode={Boolean(onSelect)}
                                    selectedItemIds={onSelect ? selectedIds : undefined}
                                    onItemSelect={onSelect ?? undefined}
                                    highlightedItemId={highlightedItemId}
                                    compact
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );

    const canCopy = leftSelectedEntries.length > 0 && Boolean(rightDraftName) && leftDraftName !== rightDraftName && !isCopying && !isHistoryAction;
    const canUndo = undoStack.length > 0 && !isCopying && !isHistoryAction;
    const canRedo = redoStack.length > 0 && !isCopying && !isHistoryAction;
    const middleActionButtonClass = 'flex items-center justify-center w-10 h-10 rounded-full shadow-lg transition-colors';
    const historyButtonClass = 'flex items-center justify-center w-8 h-8 rounded-full shadow-lg transition-colors';
    const copyCount = leftSelectedEntries.length;
    const copyButtonTitle = canCopy
        ? `Copy ${copyCount} selected ${copyCount === 1 ? 'entry' : 'entries'} to target draft`
        : 'Select entries in the source draft to copy';

    return (
        <div className="flex flex-col h-full px-2">
            <p className="text-sm text-gray-600 mb-4">
                Click to select, ⌘/Ctrl+click to add or remove, or drag to select multiple entries, then copy to the target draft.
            </p>
            {marquee && (
                <div
                    className="fixed border-2 border-blue-500 bg-blue-500/20 pointer-events-none z-[100]"
                    style={{
                        left: Math.min(marquee.startX, marquee.currentX),
                        top: Math.min(marquee.startY, marquee.currentY),
                        width: Math.abs(marquee.currentX - marquee.startX),
                        height: Math.abs(marquee.currentY - marquee.startY),
                    }}
                />
            )}
            <SharedScrollProvider>
                <div className="flex flex-1 gap-3 min-h-0 items-stretch">
                    {renderPanel(
                        'left',
                        leftDraftName,
                        leftFilteredEntries,
                        leftSelectedIds,
                        handleLeftSelect,
                        leftLoading,
                        handleSourcePanelMouseDown
                    )}

                    <div className="flex flex-col items-center justify-center gap-2 px-1 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => { void handleCopyLeftToRight(); }}
                            disabled={!canCopy}
                            title={copyButtonTitle}
                            aria-label={copyButtonTitle}
                            className={`${middleActionButtonClass} ${canCopy
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                        >
                            <span className="flex flex-col items-center gap-0.5">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </span>
                        </button>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => { void handleUndo(); }}
                                disabled={!canUndo}
                                title="Undo last copy"
                                aria-label="Undo last copy"
                                className={`${historyButtonClass} ${canUndo
                                    ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={() => { void handleRedo(); }}
                                disabled={!canRedo}
                                title="Redo last copy"
                                aria-label="Redo last copy"
                                className={`${historyButtonClass} ${canRedo
                                    ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {renderPanel('right', rightDraftName, rightFilteredEntries, [], null, rightLoading)}
                </div>
            </SharedScrollProvider>
        </div>
    );
};

export default CopyDraftView;
